import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { BsImage } from "react-icons/bs";
import * as yup from "yup";
import CollectionService from "../../services/collection.service";
import useDialogState, { Loader } from "../../hook";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { notifyError, notifySuccess } from "../../hook/notification";

// Custom Dropdown component
const Dropdown = ({ options, onSelect, selectedValue, name }) => {
  const menuRef = useRef();
  const [toggleShow, setToggleShow] = useState(false);

  const userToggle = () => {
    setToggleShow((prev) => !prev);
  };

  const handleOutsideClick = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setToggleShow(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 relative">
      <div onClick={userToggle} className="input cursor-pointer">
        {selectedValue === "" ? "Select" : selectedValue}
      </div>
      <div
        className={`${
          toggleShow ? "block" : "hidden"
        } bg-gray-800 rounded-6 overflow-hidden absolute top-[40px] w-full z-70 min-h-10 max-h-52 h-auto overflow-y-auto`}
      >
        {options.map((option, index) => (
          <p
            key={index}
            name={name}
            onClick={() => {
              onSelect(option.value, option.label);
              setToggleShow(false);
            }}
            className="bg-transparent cursor-pointer hover:bg-gray-600 p-2 text-white"
          >
            {option.label}
          </p>
        ))}
      </div>
    </div>
  );
};

const DisplayModeOption = [
  { label: "4D", value: "FOURD" },
  { label: "4K", value: "FOURK" },
  { label: "Live", value: "LIVEWALLPAPER" },
  // { label: "3D", value: "THREED" },
  { label: "HDR", value: "HDR" },
];

const AddCollection = () => {
  const location = useLocation();
  const menuRef = useRef();
  const navigate = useNavigate();
  const hiddenFileInput = useRef(null);
  const [showVal, setShowVal] = useState("4D");
  const [fileName, setFileName] = useState("");

  const {
    loader: loader,
    handleClickOpen: setLoader,
    handleClose: unSetLoader,
  } = useDialogState();

  const schema = yup.object().shape({
    // CollectionPhoto: yup.mixed().required("Please upload photo"),
    collectionName: yup.string().required("Please enter name"),
    displayMode: yup.string().required("Please select display mode"),
  });

  const initialValue = {
    CollectionPhoto: "",
    collectionName: "",
    displayMode: "FOURD",
    id: "",
    position: 0,
  };

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: schema,
    onSubmit: async (values, action) => {
      const { collectionName, displayMode, CollectionPhoto, id, position } = values;
      if (!values.id) {
        try {
          const emptyFile = new File([""], "empty.txt", { type: "text/plain" });
          const fileData = new FormData();
          fileData.append("collectionName", collectionName);
          fileData.append("displayMode", displayMode);
          fileData.append("previewImage", CollectionPhoto || emptyFile);
          setLoader();
          await CollectionService.createCollection(fileData);
          notifySuccess("Collection create successfully");
          setTimeout(() => {
            setFileName("");
            unSetLoader();
            navigate("/collections");
            action.resetForm();
          }, 1500);
        } catch (error) {
          notifyError(
            error?.response?.data?.apierror?.message || "Something went wrong"
          );
          setTimeout(() => {
            setFileName("");
            unSetLoader();
            navigate("/collections");
            action.resetForm();
          }, 1500);
          console.error("create error", error);
          unSetLoader();
        }
      } else {
        try {
          const newPosition = position === 1 ? 0 : position - 1;
          const fileData = new FormData();
          if (CollectionPhoto !== "") {
            fileData.append("previewImage", CollectionPhoto);
          }
          fileData.append("id", id);
          fileData.append("collectionName", collectionName);
          fileData.append("position", newPosition);
          fileData.append("displayMode", displayMode);
          setLoader();
          await CollectionService.updateCollection(fileData);
          notifySuccess("Collection update successfully");
          setTimeout(() => {
            setFileName("");
            unSetLoader();
            navigate("/collections");
            action.resetForm();
          }, 1500);
        } catch (error) {
          console.error("create error", error);
          notifyError(
            error?.response?.data?.apierror?.message || "Something went wrong"
          );
          unSetLoader();
        }
      }
    },
  });

  const handleFile = (file) => {
    setFileName(file.name);
  };

  const handleSelect = (value, label) => {
    setShowVal(label);
    formik.setFieldValue("displayMode", value);
  };

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const handleChange = (event) => {
    const fileUploaded = event.target.files[0];
    formik.setFieldValue("CollectionPhoto", fileUploaded);
    handleFile(fileUploaded);
  };

  useEffect(() => {
    if (location.state !== null) {
      const data = location.state;
      formik.setFieldValue("CollectionPhoto", "");
      formik.setFieldValue("id", data.id);
      formik.setFieldValue("collectionName", data.collectionName);
      formik.setFieldValue("position", data.position + 1);
      formik.setFieldValue("displayMode", data.displayMode);
      setFileName(
        data.previewurl.split("/").pop().split("-").slice(1).join("-")
      );
      const displayModeLabel =
        DisplayModeOption.find((opt) => opt.value === data.displayMode)
          ?.label || "";
      setShowVal(displayModeLabel);
    }
  }, [location]);

  return (
    <>
      <div
        className="max-w-[900px] min-w-[300px] w-auto px-5 pb-10 py-10"
        ref={menuRef}
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="flex flex-col gap-2 mb-7">
            <label className="label">Collection Name</label>
            <input
              className="input"
              placeholder="collection name"
              name="collectionName"
              value={formik.values.collectionName}
              onChange={formik.handleChange}
              type="text"
            />
            {formik.errors.collectionName && formik.touched.collectionName && (
              <div className="text-red text-sm">
                {formik.errors.collectionName}
              </div>
            )}
          </div>
          {location.state !== null && (
            <div className="flex flex-col gap-2 mb-7">
              <label className="label">Position</label>
              <input
                className="input"
                placeholder="Enter position"
                name="position"
                value={formik.values.position}
                onChange={formik.handleChange}
                type="number"
              />
            </div>
          )}

          <div className="flex flex-col gap-2 mb-7">
            <label className="label">Display Mode</label>
            <Dropdown
              options={DisplayModeOption}
              onSelect={(value, label) => handleSelect(value, label)}
              selectedValue={showVal}
              name="displayMode"
            />
            {formik.errors.displayMode && formik.touched.displayMode && (
              <div className="text-red text-sm">
                {formik.errors.displayMode}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 mb-7">
            <label className="label">Preview Image</label>
            <div className="flex items-center gap-5">
              <input type="image" accept="image/*" id="upload" hidden />
              <label
                onClick={handleClick}
                className="border border-white rounded-6 flex items-center justify-center flex-col gap-5 p-4 cursor-pointer w-full lg:p-10 text-center"
              >
                <BsImage className="text-gray-600 text-5xl" />
                <span className="text-white text-sm  ">
                  {fileName === "" ? (
                    <>
                      <span className="text-center">
                        <strong>Upload a file </strong>
                        or drag and drop <br />
                        PNG, JPG, GIF
                      </span>
                    </>
                  ) : (
                    fileName
                  )}
                </span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleChange}
                ref={hiddenFileInput}
                className="hidden"
                name="CollectionPhoto"
              />
            </div>
            {formik.errors.CollectionPhoto &&
              formik.touched.CollectionPhoto && (
                <div className="text-red text-sm mb-4">
                  {formik.errors.CollectionPhoto}
                </div>
              )}
          </div>
          <div className="flex justify-end gap-4 items-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn cancel"
            >
              Cancel
            </button>

            <button type="submit" className="btn save">
              {loader ? <Loader /> : "Save"}
            </button>
            <ToastContainer autoClose={1500} />
          </div>
        </form>
      </div>
    </>
  );
};

export default AddCollection;
